import React, { useEffect, useState, useCallback } from 'react';
import { getAdminRoles, toggleRoleStatus } from '../../services/adminApi';
import ReusableAdminTable from './ReusableAdminTable';
import { useNavigate } from 'react-router-dom';
import { deleteRoleApi } from '../../services/api';
import Swal from 'sweetalert2';
import useRoleColumns from './roles/components/useRoleColumns';

const AdminRoleList = () => {
    const [roles, setRoles] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const navigate = useNavigate();

    const loadRoles = useCallback(async () => {
        setIsLoading(true);
        try {
            const res = await getAdminRoles();
            setRoles(res.data);
        } catch (error) {
            console.error("Error fetching roles", error);
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        loadRoles();
    }, [loadRoles]);

    const handleEdit = useCallback((localId) => {
        navigate(`/admin/post-roles/${localId}`);
    }, [navigate]);

    const handleDelete = useCallback(async (localId, roleName) => {
        const result = await Swal.fire({
            title: 'Delete Role?',
            text: `Are you sure you want to delete "${roleName}"?`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes, Delete',
            cancelButtonText: 'Cancel',
            confirmButtonColor: '#c30000',
            cancelButtonColor: '#374151',
            background: '#111827',
            color: '#fff'
        });

        if (!result.isConfirmed) return;

        try {
            await deleteRoleApi(localId);

            await Swal.fire({
                icon: 'success',
                title: 'Deleted!',
                text: 'Role deleted successfully.',
                timer: 1800,
                showConfirmButton: false,
                background: '#111827',
                color: '#fff'
            });

            loadRoles();
        } catch (err) {
            Swal.fire({
                icon: 'error',
                title: 'Delete Failed',
                text: 'Failed to delete role.',
                background: '#111827',
                color: '#fff'
            });
        }
    }, [loadRoles]);

    const handleAddNew = () => {
        navigate('/admin/post-roles');
    };

    const handleToggleStatus = useCallback(async (id, role) => {
        try {
            const isActive = role.status === 'active';

            const result = await Swal.fire({
                title: isActive ? 'Deactivate Role?' : 'Activate Role?',
                text: isActive ? 'This role will become inactive.' : 'This role will become active.',
                icon: 'warning',
                showCancelButton: true,
                confirmButtonText: isActive ? 'Yes, Deactivate' : 'Yes, Activate',
                cancelButtonText: 'Cancel',
                confirmButtonColor: isActive ? '#c30000' : '#16a34a',
                cancelButtonColor: '#374151',
                background: '#111827',
                color: '#fff'
            });

            if (!result.isConfirmed) return;

            const res = await toggleRoleStatus(id);

            if (res.data.block) {
                Swal.fire({
                    icon: 'warning',
                    title: 'Action Blocked',
                    text: res.data.message,
                    background: '#111827',
                    color: '#fff'
                });
                return;
            }

            await Swal.fire({
                icon: 'success',
                title: 'Updated!',
                text: isActive ? 'Role deactivated successfully.' : 'Role activated successfully.',
                timer: 1800,
                showConfirmButton: false,
                background: '#111827',
                color: '#fff'
            });

            loadRoles();
        } catch (err) {
            Swal.fire({
                icon: 'error',
                title: 'Something Went Wrong',
                text: 'Failed to update role status.',
                background: '#111827',
                color: '#fff'
            });
        }
    }, [loadRoles]);

    const columns = useRoleColumns({ handleToggleStatus, handleEdit, handleDelete });

    return (
        <ReusableAdminTable
            data={roles}
            columns={columns}
            isLoading={isLoading}
            title="Role Management"
            onAddNew={handleAddNew}
            addNewButtonText="Add Role"
            searchPlaceholder="Search roles..."
            exportCsvFilename="roles.csv"
        />
    );
};

export default AdminRoleList;